import { Head, router } from "@inertiajs/react"
import { useState } from "react"

import { FilterChip, FilterRow } from "@/components/admin/filter-chip"
import { PageHeader } from "@/components/console/page-header"
import { Table, TableCard } from "@/components/ui/table"
import { TabList, Tabs } from "@/components/ui/tabs"
import { AdminLayout } from "@/layouts/admin-layout"
import { adminFixQueuePath, adminSchoolPath } from "@/routes"

import type { FixItem, FixKind } from "./types"

interface Props {
  items: FixItem[]
  kinds: { key: FixKind; label: string; count: number }[]
  scopes: { id: number; label: string }[]
  filters: { scope_id: number | null }
}

/** The rows whoever fixes the data needs: routed here, not hidden. The fix happens in Salesforce. */
export default function AdminFixQueueIndex({
  items,
  kinds,
  scopes,
  filters,
}: Props) {
  const [kind, setKind] = useState<FixKind>(kinds[0]?.key ?? "owner_inactive")
  const rows = items.filter((i) => i.kind === kind)
  const visitScope = (scopeId: number | null) =>
    router.visit(adminFixQueuePath(), {
      data: scopeId ? { scope_id: scopeId } : {},
      preserveState: true,
      preserveScroll: true,
      only: ["items", "kinds", "filters"],
    })

  const kindLabel = kinds.find((k) => k.key === kind)?.label ?? kind
  const scope = scopes.find((s) => s.id === filters.scope_id)
  const tokens = scope
    ? [
        {
          label: `scope: ${scope.label.toLowerCase()}`,
          onClear: () => visitScope(null),
        },
      ]
    : []

  return (
    <AdminLayout>
      <Head title="Fix queue" />
      <div className="mx-auto flex max-w-7xl flex-col gap-6 px-4 lg:px-8">
        <PageHeader
          crumbs={[{ label: "call list" }, { label: "fix queue" }]}
          title="Fix queue"
          description="Owners who left, accounts with no institution id, deliverables with programs remaining and no region, call logs Salesforce refused."
        />

        {scopes.length > 0 && (
          <FilterRow label="Scope">
            <FilterChip
              active={!filters.scope_id}
              onClick={() => visitScope(null)}
            >
              All
            </FilterChip>
            {scopes.map((s) => (
              <FilterChip
                key={s.id}
                active={filters.scope_id === s.id}
                onClick={() => visitScope(s.id)}
              >
                {s.label}
              </FilterChip>
            ))}
          </FilterRow>
        )}

        <Tabs
          selectedKey={kind}
          onSelectionChange={(k) => setKind(String(k) as FixKind)}
        >
          <TabList
            size="sm"
            type="underline"
            items={kinds.map((k) => ({
              id: k.key,
              label: k.label,
              badge: k.count,
            }))}
          />
        </Tabs>

        <TableCard.Root bleed>
          <TableCard.Caption
            title={kindLabel}
            count={`${rows.length} to fix`}
            tokens={tokens}
          />
          {rows.length === 0 ? (
            <TableCard.Empty>
              Nothing to fix here; this kind is clear.
            </TableCard.Empty>
          ) : (
            <>
              <Table aria-label="Fix queue">
                <Table.Header>
                  <Table.Head
                    id="name"
                    isRowHeader
                    label="Record"
                    className="w-full"
                  />
                  <Table.Head id="detail" label="What" />
                  <Table.Head id="open" label="Open" />
                </Table.Header>
                <Table.Body items={rows}>
                  {(row) => (
                    <Table.Row
                      id={row.salesforce_id}
                      href={
                        row.school_id
                          ? adminSchoolPath(row.school_id)
                          : undefined
                      }
                    >
                      <Table.Lead>{row.name}</Table.Lead>
                      <Table.Cell className="whitespace-normal">
                        {row.detail}
                      </Table.Cell>
                      <Table.Cell>
                        {row.salesforce_url ? (
                          <Table.Link
                            href={row.salesforce_url}
                            target="_blank"
                            rel="noreferrer"
                          >
                            Salesforce ↗
                          </Table.Link>
                        ) : (
                          "—"
                        )}
                      </Table.Cell>
                    </Table.Row>
                  )}
                </Table.Body>
              </Table>
              <TableCard.Footer total={rows.length} />
            </>
          )}
        </TableCard.Root>
      </div>
    </AdminLayout>
  )
}
